import { Injectable } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { BehaviorSubject, MonoTypeOperatorFunction, Observable, of } from 'rxjs';
import { delay, map, switchMap, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class SpinnerCoverService {
  public isLoading$: Observable<boolean>;
  public isNotLoading$: Observable<boolean>;
  public isLoadingDebounce$: Observable<boolean>;

  private isLoadingSubj$ = new BehaviorSubject(false);
  private readonly activeLoadingKeys = new Map<string, number>();
  private readonly defaultLoadingKey = 'global';

  constructor(private router: Router) {
    this.isLoading$ = this.isLoadingSubj$.asObservable();
    this.isNotLoading$ = this.isLoading$.pipe(map((x) => !x));
    // Show spinner immediately on load start and delay hide by 75ms to prevent flicker.
    this.isLoadingDebounce$ = this.isLoading$.pipe(
      switchMap((isLoading) => (isLoading ? of(true) : of(false).pipe(delay(75))))
    );

    this.router.events?.subscribe((event) => {
      // Reset active loading flags on browser history/hash navigation
      // Browser back/forward can leave previous-page requests in flight, so clear spinner state on these navigations.
      if (event instanceof NavigationStart && event.navigationTrigger !== 'imperative') {
        this.forceEnd();
      }
    });
  }

  public startInSequence = <T>(key: string = this.defaultLoadingKey): MonoTypeOperatorFunction<T> =>
    tap(() => this.start(key));

  public endInSequence = <T>(key: string = this.defaultLoadingKey): MonoTypeOperatorFunction<T> =>
    tap(() => this.end(key));

  public start = (key: string = this.defaultLoadingKey) => {
    const currentCount = this.activeLoadingKeys.get(key) || 0;
    this.activeLoadingKeys.set(key, currentCount + 1);
    this.updateLoadingState();
  };

  public end = (key: string = this.defaultLoadingKey) => {
    const currentCount = this.activeLoadingKeys.get(key) || 0;
    if (currentCount <= 1) {
      this.activeLoadingKeys.delete(key);
    } else {
      this.activeLoadingKeys.set(key, currentCount - 1);
    }
    this.updateLoadingState();
  };

  public resetKey = (key: string = this.defaultLoadingKey) => {
    this.activeLoadingKeys.delete(key);
    this.updateLoadingState();
  };

  private readonly forceEnd = () => {
    this.activeLoadingKeys.clear();
    this.updateLoadingState();
  };

  private updateLoadingState() {
    this.isLoadingSubj$.next(this.activeLoadingKeys.size > 0);
  }
}
